/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { Typography, Divider, Paper, CircularProgress, Hidden, Chip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import NumberUtils from 'commons/utils/numberUtils';
import { STRIPE_CLIENT_KEY } from 'commons/constants';
import GetPremiumButton from 'components/buttons/GetPremiumButton';
import showNotification from 'commons/helpers/showNotification';

import PlanDiscountInfo from './PlanDiscountInfo';
import { PERIOD_TYPE_MONTHLY, PERIOD_TYPE_YEARLY, getPeriodTypeSingularLabel } from './periodTypeUtils';
import moment from 'moment';

const useStyles = makeStyles()((theme) => ({
  rootContainerNoBox: {
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(8),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  rootContainer: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(8),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  containerBox: {
    padding: theme.spacing(0),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
      color: '#263238',
      transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      backgroundColor: '#FFFFFF',
      boxShadow:
        '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
      borderRadius: '4px',
    },
  },
  containerNoBox: {
    padding: theme.spacing(0),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
      borderRadius: '4px',
    },
  },
  plansContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  planBox: {
    background: 'linear-gradient(180deg, #5721E5 30%, #8C21E5 90%)',
    padding: '40px 25px',
    overflow: 'visible',
    position: 'relative',
    transition: 'transform 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
    width: '310px',
    marginLeft: '15px',
    marginRight: '15px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    marginTop: '12px',
    [theme.breakpoints.down('sm')]: {
      marginLeft: '0px',
      marginRight: '0px',
      marginBottom: '15px',
      marginTop: '0px',
    },
  },
  planBoxBlackFriday: {
    background: 'linear-gradient(45deg, #00ffa1 0%, #5721E5 100%)',
    padding: '40px 25px',
    overflow: 'visible',
    position: 'relative',
    transition: 'transform 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
    width: '310px',
    marginLeft: '15px',
    marginRight: '15px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    marginTop: '12px',
    [theme.breakpoints.down('sm')]: {
      marginLeft: '0px',
      marginRight: '0px',
      marginBottom: '15px',
      marginTop: '0px',
    },
  },
  planBoxNewYear: {
    // background: 'linear-gradient(45deg, #8C21E5 0%, #22b8e5 100%)',
    background: 'linear-gradient(45deg, #00ffa1 0%, #5721E5 100%)',
    padding: '40px 25px',
    overflow: 'visible',
    position: 'relative',
    transition: 'transform 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
    width: '310px',
    marginLeft: '15px',
    marginRight: '15px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    marginTop: '12px',
    [theme.breakpoints.down('sm')]: {
      marginLeft: '0px',
      marginRight: '0px',
      marginBottom: '15px',
      marginTop: '0px',
    },
  },
  planBoxTextColor: {
    color: 'white',
  },
  divider: {
    marginBottom: '10px',
    marginTop: '10px',
  },
  header: {
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '10px',
    },
  },
  premiumButtonSinglePlan: {
    margin: '10px auto auto auto',
    textAlign: 'center',
    maxWidth: '320px',
  },
  discountChip: {
    backgroundColor: '#2ED195',
    color: 'white',
    fontWeight: 'bold',
  },
  welcomeHeader: {
    background: 'linear-gradient(180deg, #5721E5 30%, #8C21E5 90%)',
    color: 'white',
    transition: 'transform 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  imgRemoveExpiredMsg: {
    maxWidth: '380px',
    textAlign: 'center',
    marginTop: '10px',
  },
  textRemoveExpiredMsg: { marginTop: '10px', color: '#8C21E5' },

  planBadge: {
    position: 'absolute',
    display: 'block',
    width: '200px',
    padding: '10px 0px',
    backgroundColor: '#FF1212',
    color: 'white',
    fontWeight: '500',
    fontSize: '15px',
    lineHeight: '16px',
    textShadow: 'rgb(0 0 0 / 20%) 0px 1px 1px',
    textAlign: 'center',
    left: '-21px',
    top: '30px',
    transform: 'rotate(45deg)',
  },
  planBadgeBlackFriday: {
    position: 'absolute',
    display: 'block',
    width: '200px',
    padding: '10px 0px',
    backgroundColor: 'black',
    lineHeight: '16px',
    textShadow: 'rgb(0 0 0 / 20%) 0px 1px 1px',
    textAlign: 'center',
    left: '-21px',
    top: '30px',
    transform: 'rotate(45deg)',
    color: 'yellow',
    fontWeight: 'bold',
    fontSize: '17px',
  },
  planBadgeNewYear: {
    position: 'absolute',
    display: 'block',
    width: '200px',
    padding: '10px 0px',
    // backgroundColor: '#2ED195',
    backgroundColor: '#FF1212',
    lineHeight: '16px',
    textShadow: 'rgb(0 0 0 / 20%) 0px 1px 1px',
    textAlign: 'center',
    left: '-21px',
    top: '30px',
    transform: 'rotate(45deg)',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '17px',
  },
  badgeContainer: {
    width: '120px',
    height: '120px',
    overflow: 'hidden',
    position: 'absolute',
    top: '-1px',
    right: '-1px',
  },
  textFooterInfo: {
    textAlign: 'center',
    marginTop: '15px',
  },
  newPriceLabel: {
    color: 'white',
    marginRight: '4px',
  },
  oldPriceLabel: {
    color: 'white',
    marginRight: '4px',
    textDecoration: 'line-through',
    textDecorationThickness: '1px',
  },

  lblPromoTime: {
    textAlign: 'center',
    color: 'rgb(255 0 0)',
    fontWeight: '500',
    fontSize: '11px',
  },
  containerGetPremiumButton: { textAlign: 'center', marginTop: '10px' },
}));

// COMPONENT
function StripeGetPremium({
  noBox,
  isLoadingPlan,
  planList,
  isAlreadyPremium,
  createSessionForPlan,
  onSelectPlan,
  customHeaderTitle,
}) {
  const { classes } = useStyles();
  const [isLoadingPaymentSession, setIsLoadingPaymentSession] = useState(false);

  const handleStartPaymentSession = (selectedPlan) => {
    if (onSelectPlan) {
      onSelectPlan(selectedPlan);
      return;
    }
    // eslint-disable-next-line no-undef
    if (window.Stripe == null) {
      showNotification(
        'error',
        'Não foi possivel comunicar com o portal de pagamentos. Recarregue o naveguedor ou entre em contato com o suporte.',
      );
      return;
    }

    setIsLoadingPaymentSession(true);
    createSessionForPlan(selectedPlan.stripePlan.id)
      .then((resp) => {
        // eslint-disable-next-line no-undef
        const stripe = window.Stripe(STRIPE_CLIENT_KEY);

        stripe
          .redirectToCheckout({
            sessionId: resp.sessionId,
          })
          .then(() => {
            // console.log('loaded');
            setIsLoadingPaymentSession(false);
          })
          .catch(() => {
            setIsLoadingPaymentSession(false);
          });
      })
      .catch(() => {
        setIsLoadingPaymentSession(false);
      });
  };

  return (
    <div className={noBox ? classes.rootContainerNoBox : classes.rootContainer}>
      <div className={noBox ? classes.containerNoBox : classes.containerBox}>
        <div>
          {isAlreadyPremium && (
            <>
              <Paper className={classes.welcomeHeader}>
                <FontAwesomeIcon icon="crown" size="2x" fixedWidth color="#fcdf03" />
                <Typography variant="h3" style={{ color: 'white', marginTop: '10px' }}>
                  Você já é usuário premium
                </Typography>
              </Paper>

              <div style={{ textAlign: 'center' }}>
                <Typography variant="h4" style={{ marginTop: '20px' }}>
                  Se o app ainda estiver mostrando a mensagem de expiração, é normal, demora um pouquinho pra
                  sair.
                </Typography>

                <Typography variant="h5" className={classes.textRemoveExpiredMsg}>
                  Caso queira forçar o app a remover a mensagem, basta clicar aqui (veja abaixo).
                </Typography>

                <img
                  alt="Como atualizar a mensagem de expiração no app"
                  className={classes.imgRemoveExpiredMsg}
                  src="/images/premium/howToRefreshTrialMsg.png"
                />
              </div>
            </>
          )}

          {!isAlreadyPremium && (
            <>
              <div className={classes.header}>
                {customHeaderTitle ? (
                  customHeaderTitle
                ) : (
                  <>
                    <Typography variant="h3">Torne-se Premium e impulsione o seu negócio.</Typography>

                    <Hidden only={['xs']}>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        className="sm-hidden"
                        style={{ marginTop: '10px' }}
                      >
                        MinhaAgenda pensada em você empreendedor, para que possa agilizar o seu dia,
                        economizar tempo, conhecer e entender o seu negócio, sem ter que contratar serviços
                        pesados e difíceis.
                      </Typography>
                    </Hidden>
                  </>
                )}
              </div>
            </>
          )}

          {/* LOADING */}
          {isLoadingPlan && (
            <Paper elevation={3} className={classes.planBox}>
              <div style={{ textAlign: 'center' }}>
                <Typography variant="h3" gutterBottom className={classes.planBoxTextColor}>
                  Carregando detalhes...
                </Typography>
                <CircularProgress size={50} style={{ marginTop: '20px', color: 'white' }} thickness={5} />
              </div>
            </Paper>
          )}

          {/* PREMIUM */}
          {!isLoadingPlan && !isAlreadyPremium && planList && planList.length > 0 && (
            <>
              <div className={classes.plansContainer}>
                {planList.map((planDetails) => {
                  const isBlackFridayPromo =
                    planDetails?.stripeDiscount?.internalDescription?.includes('BlackFriday') ?? false;
                  const isNewYearPromo =
                    planDetails?.stripeDiscount?.internalDescription?.includes('AnoNovo') ?? false;
                  const isSpecial = planDetails.stripePlan.oldPrice != null;

                  const isSpecialPromo =
                    isBlackFridayPromo ||
                    isNewYearPromo ||
                    planDetails.stripePlan.periodType === PERIOD_TYPE_YEARLY;

                  return (
                    <Paper
                      elevation={3}
                      className={
                        isSpecialPromo || isSpecial
                          ? isBlackFridayPromo
                            ? classes.planBoxBlackFriday
                            : classes.planBoxNewYear
                          : classes.planBox
                      }
                      key={planDetails.stripePlan.id}
                    >
                      {isBlackFridayPromo ? (
                        <div className={classes.badgeContainer}>
                          <Typography variant="body2" className={classes.planBadgeBlackFriday}>
                            Black Friday
                          </Typography>
                        </div>
                      ) : (
                        <>
                          {isSpecialPromo && (
                            <div className={classes.badgeContainer}>
                              <Typography variant="body2" className={classes.planBadgeNewYear}>
                                Imperdível 🥂
                              </Typography>
                            </div>
                          )}
                        </>
                      )}

                      {!isSpecialPromo && planDetails.stripePlan.badgeText && (
                        <div className={classes.badgeContainer}>
                          <Typography variant="body2" className={classes.planBadge}>
                            {planDetails.stripePlan.badgeText}
                          </Typography>
                        </div>
                      )}

                      <Typography variant="h3" gutterBottom className={classes.planBoxTextColor}>
                        {planDetails.stripePlan.name}
                      </Typography>

                      {planDetails.stripeDiscount != null ? (
                        <PlanDiscountInfo
                          stripePlan={planDetails.stripePlan}
                          discountInfo={planDetails.stripeDiscount}
                          isBlackFridayPromo={isBlackFridayPromo}
                          isNewYearPromo={isNewYearPromo}
                        />
                      ) : (
                        <div>
                          {planDetails.stripePlan.oldPrice != null ? (
                            <>
                              <Typography
                                variant="h4"
                                gutterBottom
                                component="span"
                                className={classes.oldPriceLabel}
                              >
                                De: {NumberUtils.toCurrency(planDetails.stripePlan.oldPrice, 2)}
                              </Typography>
                              <div style={{}}>
                                <Typography
                                  variant="h2"
                                  gutterBottom
                                  component="span"
                                  className={classes.newPriceLabel}
                                >
                                  Por: {NumberUtils.toCurrency(planDetails.stripePlan.price, 2)}
                                </Typography>
                                <Typography
                                  variant="h6"
                                  gutterBottom
                                  component="span"
                                  className={classes.planBoxTextColor}
                                >
                                  {' '}
                                  / {getPeriodTypeSingularLabel(planDetails.stripePlan.periodType)}
                                </Typography>
                              </div>
                            </>
                          ) : (
                            <>
                              <Typography
                                variant="h3"
                                gutterBottom
                                component="span"
                                className={classes.planBoxTextColor}
                              >
                                {NumberUtils.toCurrency(planDetails.stripePlan.price, 2)}
                              </Typography>
                              <Typography
                                variant="h6"
                                gutterBottom
                                component="span"
                                className={classes.planBoxTextColor}
                              >
                                {' '}
                                / {getPeriodTypeSingularLabel(planDetails.stripePlan.periodType)}
                              </Typography>
                            </>
                          )}

                          {planDetails.stripePlan.periodType !== PERIOD_TYPE_MONTHLY &&
                            planDetails.stripePlan.monthlyAveragePrice && (
                              <Typography variant="h6" gutterBottom className={classes.planBoxTextColor}>
                                Em média{' '}
                                {NumberUtils.toCurrency(planDetails.stripePlan.monthlyAveragePrice, 2)} por
                                mês
                              </Typography>
                            )}

                          {planDetails.stripePlan.highlightMessage && (
                            <Chip
                              label={planDetails.stripePlan.highlightMessage}
                              className={classes.discountChip}
                            />
                          )}
                        </div>
                      )}

                      <Hidden only={planList.length === 1 ? null : ['xs']}>
                        <Divider className={classes.divider} />
                        <Typography variant="subtitle2" className={classes.planBoxTextColor}>
                          - Agendamentos ilimitados
                        </Typography>
                        <Typography variant="subtitle2" className={classes.planBoxTextColor}>
                          - Mensagens Whats
                        </Typography>
                        <Typography variant="subtitle2" className={classes.planBoxTextColor}>
                          - Rico conjunto de relatórios
                        </Typography>
                        <Typography variant="subtitle2" className={classes.planBoxTextColor}>
                          - Armazenamento de dados na nuvem
                        </Typography>
                        <Typography variant="subtitle2" className={classes.planBoxTextColor}>
                          - Segurança e backup dos seus dados
                        </Typography>
                        <Typography variant="subtitle2" className={classes.planBoxTextColor}>
                          - Suporte prioritário
                        </Typography>
                      </Hidden>

                      {planList.length > 1 && (
                        <>
                          <Divider className={classes.divider} />

                          <div className={classes.containerGetPremiumButton}>
                            <GetPremiumButton
                              label="Escolher Plano"
                              onClick={() => {
                                handleStartPaymentSession(planDetails);
                              }}
                              isLoading={isLoadingPaymentSession}
                            />
                            {(isNewYearPromo || isSpecial) && (
                              <Typography className={classes.lblPromoTime}>
                                Válida apenas até {moment().format('MMMM [de] YYYY')}
                              </Typography>
                            )}
                          </div>
                        </>
                      )}
                    </Paper>
                  );
                })}
              </div>
              {planList.length === 1 && (
                <div className={classes.premiumButtonSinglePlan}>
                  <GetPremiumButton
                    label="Quero ser premium"
                    onClick={() => {
                      handleStartPaymentSession(planList[0]);
                    }}
                    isLoading={isLoadingPaymentSession}
                  />
                </div>
              )}
              <div className={classes.textFooterInfo}>
                <Typography variant="body2" gutterBottom>
                  Clique para adquirir o plano. Você pode cancelar a hora que quiser, acessando o menu
                  configurações.
                </Typography>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

StripeGetPremium.defaultProps = {
  noBox: false,
};

export default StripeGetPremium;
